import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import { User } from 'firebase/auth';
import styled from 'styled-components';

import { Color } from '@assets/constants';
import { AppConfigContainer } from '@containers/adminPanel/AppConfigContainer';
import { DiscountsMenuContainer } from '@containers/adminPanel/DiscountsContainer';
import { LabelsContainer } from '@containers/adminPanel/LabelsContainer';
import { ManageProductsContainer } from '@containers/adminPanel/ManageProductsContainer';
import { NewProductContainer } from '@containers/adminPanel/NewProductContainer';
import { PromoCodesContainer } from '@containers/adminPanel/PromoCodesContainer';
import { UpdateBannersContainer } from '@containers/adminPanel/UpdateBannersContainer';
import { UpdateShippingCostContainer } from '@containers/adminPanel/UpdateShippingCostContainer';
import { useAuth, useCustomNavigate } from '@hooks';
import { Button, ButtonSize, ButtonType } from '@ui/Button';

interface Props {
  user: User;
}

export const AdminPanelMenuContainer = ({ user }: Props) => {
  const [showAddNewProduct, setShowAddNewProduct] = useState<boolean>(false);
  const [showManageProducts, setShowManageProducts] = useState<boolean>(false);
  const [showUpdateBannerImage, setShowUpdateBannerImage] =
    useState<boolean>(false);
  const [showLabels, setShowLabels] = useState<boolean>(false);
  const [showDiscounts, setShowDiscounts] = useState<boolean>(false);
  const [showUpdateShippingCost, setShowUpdateShippingCost] =
    useState<boolean>(false);
  const [showPromoCodes, setShowPromoCodes] = useState<boolean>(false);
  const [showAppConfig, setShowAppConfig] = useState<boolean>(false);

  const { signOut } = useAuth();
  const navigate = useCustomNavigate();

  const isPageSelected =
    showAddNewProduct ||
    showManageProducts ||
    showLabels ||
    showDiscounts ||
    showUpdateBannerImage ||
    showUpdateShippingCost ||
    showPromoCodes ||
    showAppConfig;

  return (
    <Container>
      <PanelContainer>
        <Header>
          <AdminWrapper>
            <HeaderText>Signed in as</HeaderText>
            <BoldText>{user.email}</BoldText>
          </AdminWrapper>
          <Button
            label={'Sign out'}
            type={ButtonType.SECONDARY}
            size={ButtonSize.SMALL}
            backgroundColor={Color.WHITE}
            onClick={() => signOut()}
          />
        </Header>

        <Divider />

        {showAddNewProduct && <NewProductContainer />}
        {showManageProducts && <ManageProductsContainer />}
        {showUpdateBannerImage && <UpdateBannersContainer />}
        {showLabels && <LabelsContainer />}
        {showDiscounts && <DiscountsMenuContainer />}
        {showUpdateShippingCost && <UpdateShippingCostContainer />}
        {showPromoCodes && <PromoCodesContainer />}
        {showAppConfig && <AppConfigContainer />}
        {!isPageSelected && (
          <ButtonContainer>
            <OrdersButton onClick={() => navigate('/admin-panel/orders')}>
              <SparklesIcon
                src={require('../../assets/images/sparkles-icon.png')}
              />
              Orders
              <SparklesIcon
                src={require('../../assets/images/sparkles-icon.png')}
              />
            </OrdersButton>
            <Button
              label={'Калкулатор за ресто'}
              type={ButtonType.SECONDARY}
              onClick={() => navigate('/admin-panel/change-calculator')}
            />
            <Button
              label={'Add new product'}
              onClick={() => setShowAddNewProduct(true)}
            />
            <Button
              label={'Manage products'}
              onClick={() => setShowManageProducts(true)}
            />
            <Button
              label={'Labels / Categories'}
              onClick={() => setShowLabels(true)}
            />
            <Button
              label={'Discounts'}
              onClick={() => setShowDiscounts(true)}
            />
            <Button
              label={'Banners'}
              onClick={() => setShowUpdateBannerImage(true)}
            />
            <Button
              label={'Shipping cost'}
              onClick={() => setShowUpdateShippingCost(true)}
            />
            <Button
              label={'Promo codes'}
              onClick={() => setShowPromoCodes(true)}
            />
            <Button
              label={'App Config'}
              onClick={() => setShowAppConfig(true)}
            />
          </ButtonContainer>
        )}
      </PanelContainer>
      {isPageSelected && (
        <BackToMenuText
          onClick={() => {
            setShowAddNewProduct(false);
            setShowManageProducts(false);
            setShowUpdateBannerImage(false);
            setShowLabels(false);
            setShowDiscounts(false);
            setShowUpdateShippingCost(false);
            setShowPromoCodes(false);
            setShowAppConfig(false);
          }}
        >
          <p>Back to Menu</p>
        </BackToMenuText>
      )}
      <BackLinkText to={'/'}>
        <p>Back to Teniski-Varna</p>
      </BackLinkText>
    </Container>
  );
};

const SparklesIcon = styled.img`
  width: 24px;
`;

const OrdersButton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  min-width: max-content;
  white-space: nowrap;
  background: ${Color.GREEN_CHECK};
  font-weight: 500;
  color: ${Color.WHITE};
  min-height: 55px;
  min-width: 120px;
  padding-top: 16px;
  padding-bottom: 16px;
  padding-left: 20px;
  padding-right: 20px;
  border-radius: 16px;
  font-size: 16px;

  &:active {
    filter: brightness(0.9);
  }
  &:hover {
    filter: brightness(0.9);
  }
`;

const Divider = styled.div`
  border-bottom: 1px ${Color.WHITE} solid;
`;

const BackToMenuText = styled.a`
  cursor: pointer;
  text-decoration: none;
  color: ${Color.BLACK};
  padding-top: 20px;
`;

const BackLinkText = styled(Link)`
  text-decoration: none;
  color: ${Color.BLACK};
  padding-top: 20px;
`;

const ButtonContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 10px;
  padding: 20px 0;
`;

const BoldText = styled.p`
  font-weight: bold;
  color: ${Color.WHITE};
`;

const HeaderText = styled.p`
  color: ${Color.WHITE};
`;

const PanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 90%;
  max-width: 1000px;
  background: ${Color.MEDIUM_GRAY};
  border-radius: 15px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.26);
  padding: 20px;
  margin-top: 20px;
`;

const AdminWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  padding-bottom: 20px;
`;

const Container = styled.div`
  min-height: 100vh;
  width: 100vw;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;
